import { useNavigate } from "react-router-dom"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useAmendsValue } from "./hooks/use-amends-value"
import { SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION } from "./lib/schedule-editor-action-types"
import { confirmApplyAuthoringRecommendationIfDrifted } from "./lib/confirm-apply-drifted-authoring-recommendation"
import {
    applyAuthoringRecommendationToDraft,
    AUTHORING_RECOMMENDATION_STATE_APPLIED,
    AUTHORING_RECOMMENDATION_STATE_CONFLICTED,
    getAuthoringRecommendationState,
    resolveAuthoringRecommendationDraft,
} from "./lib/authoring-schedule-recommendations"
import { buildRecommendationAmendUndoState } from "./lib/recommendation-amend-undo"
import {
    canFocusAuthoringRecommendationTarget,
    focusAuthoringRecommendationTarget,
} from "./lib/focus-authoring-recommendation-target"
import { buildRecommendationCriticalityMeta } from "./lib/authoring-recommendation-row-presenters"
import { getReviewProjectsRootPath } from "routes/plugins/review-projects/review-project-route-paths"
import { getLegislationResolutionsRootPath } from "routes/schedule/plugins/legislation-resolutions/legislation-resolution-route-paths"
import { restoreDraftSnapshotInPlace } from "./lib/restore-draft-snapshot"
import { mergeAuthoringRecommendationNote } from "./controller/merge-authoring-recommendation-note"
import { buildRecommendationNoteContent, mergeRecommendationNoteContent } from "./lib/recommendation-note-content"
import {
    collectProjectBridgeAppliedTargets,
    persistProjectBridgeAppliedTargets,
} from "./lib/project-bridge-applied-recommendations"
import { AuthoringRecommendationGroupRow } from "./authoring-recommendation-group-row"
import { AuthoringRecommendationRowView } from "./authoring-recommendation-row-view"
import { useScheduleEditorDraftStructureVersion } from "./hooks/use-schedule-editor-draft-structure-version"
import { useScheduleEditorDraftEditedVersion } from "./hooks/use-schedule-editor-draft-edited-version"

export function AuthoringRecommendationRow({
    item,
    selectedRecommendationId,
    onSelectRecommendation,
    onHoverRecommendation,
    onLeaveRecommendation,
    selectedRecommendationIdsByGroupKey,
    onSelectRecommendationGroupChoice,
}) {
    const navigate = useNavigate()
    const { target, rawSchedule } = useBoundContext()
    const { projectId, scheduleId, draftId } = useScheduleEditorIds()
    const { pushUndo, saveAmend, notifyDraftStructureChanged } = useScheduleEditorActions()
    const { amends: authoringAmends } = useAmendsValue()
    useScheduleEditorDraftStructureVersion()
    useScheduleEditorDraftEditedVersion()
    const draft = resolveAuthoringRecommendationDraft({ target, rawSchedule })
    const state = getAuthoringRecommendationState({ recommendation: item, draft })
    const criticality = buildRecommendationCriticalityMeta(item)
    const applyDisabled = state === AUTHORING_RECOMMENDATION_STATE_APPLIED
    const canFocusRecommendation = canFocusAuthoringRecommendationTarget({ recommendation: item, draft: target })
    const isSelected = Boolean(item?.id) && item.id === selectedRecommendationId
    const stateColor = buildStateColor(state)

    if (item?.kind === "merged-group") {
        return (
            <AuthoringRecommendationGroupRow
                item={item}
                selectedRecommendationId={selectedRecommendationId}
                onSelectRecommendation={onSelectRecommendation}
                onHoverRecommendation={onHoverRecommendation}
                onLeaveRecommendation={onLeaveRecommendation}
                selectedRecommendationIdsByGroupKey={selectedRecommendationIdsByGroupKey}
                onSelectRecommendationGroupChoice={onSelectRecommendationGroupChoice}
            />
        )
    }

    return (
        <AuthoringRecommendationRowView
            item={item}
            rowStyles={buildRowStyles(isSelected)}
            applyDisabled={applyDisabled}
            onSelect={handleSelect}
            onHover={handleHover}
            onLeave={handleLeave}
            onOpenSource={handleOpenSource}
            onApply={handleApplyRecommendation}
            stateLabel={buildStateLabel(state)}
            stateColor={stateColor}
            stateVariant={buildChipVariant(stateColor)}
            criticality={criticality}
        />
    )

    function handleSelect() {
        onSelectRecommendation?.(item)
        if (canFocusRecommendation) {
            showInEditor()
        }
    }

    function handleHover(event) {
        onHoverRecommendation?.(item, event.currentTarget)
    }

    function handleLeave() {
        onLeaveRecommendation?.()
    }

    function handleOpenSource(event) {
        event.stopPropagation()
        openSource()
    }

    async function handleApplyRecommendation(event) {
        event.stopPropagation()
        await applyRecommendation()
    }

    async function applyRecommendation() {
        const { proceed, force } = await confirmApplyAuthoringRecommendationIfDrifted({
            recommendation: item,
            draft,
        })
        if (!proceed) return

        const snapshot = structuredClone(draft)
        const result = applyAuthoringRecommendationToDraft({ recommendation: item, draft, force })
        if (!result?.applied) {
            errorSnackbar("This recommendation no longer matches the current draft")
            return
        }

        try {
            const amendUndo = await saveRecommendationNote(result)
            const projectBridgeApplied = await persistProjectBridgeApplied()
            if (pushUndo) {
                pushUndo({
                    type: SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION,
                    snapshot,
                    amendUndo,
                    projectBridgeApplied,
                })
            }
            notifyDraftStructureChanged?.()
            successSnackbar("Recommendation applied")
        } catch (error) {
            restoreDraftSnapshotInPlace(draft, snapshot)
            notifyDraftStructureChanged?.()
            console.error("Failed to apply recommendation", error)
            errorSnackbar("Failed to apply recommendation")
        }
    }

    async function persistProjectBridgeApplied() {
        const { importId, targets } = collectProjectBridgeAppliedTargets([item])
        if (targets.length === 0) return null

        return persistProjectBridgeAppliedTargets({ importId, targets, projectId, scheduleId, draftId, applied: true })
    }

    function openSource() {
        if (!item?.routeTo) return
        navigate(getRouteToCurrentShell(item))
    }

    function showInEditor() {
        const focused = focusAuthoringRecommendationTarget({ recommendation: item, draft: target })
        if (!focused) {
            errorSnackbar("Couldn't find this item in the editor")
        }
    }

    async function saveRecommendationNote(result) {
        if (!saveAmend || !result?.amendsKey) return null

        const existing = authoringAmends.find((amend) => amend?.payload?.amendsKey === result.amendsKey) ?? null
        const prepared = await resolveRecommendationContent({
            existing,
            recommendation: item,
            projectId,
            scheduleId,
            amendsKey: result.amendsKey,
        })

        const savedAmend = await saveAmend({
            amendsKey: result.amendsKey,
            content: prepared.content,
            recommendationId: item?.id,
            sourceType: item?.sourceType,
            sourceRecordId: item?.sourceRecordId,
            sourceLabel: item?.sourceLabel,
            sourceRoute: item?.routeTo,
        })

        return buildRecommendationAmendUndoState({
            existingAmend: prepared.existingAmend,
            savedAmend,
        })
    }
}

function getRouteToCurrentShell(item) {
    const routeTo = `${item?.routeTo || ""}`
    if (!routeTo) return routeTo

    if (item?.sourceType === "reviewProject" && routeTo.startsWith("/app/review-projects")) {
        return routeTo.replace("/app/review-projects", getReviewProjectsRootPath())
    }

    if (item?.sourceType === "legislation" && routeTo.startsWith("/app/schedule-resolutions")) {
        return routeTo.replace("/app/schedule-resolutions", getLegislationResolutionsRootPath())
    }

    return routeTo
}

function buildRowStyles(isSelected) {
    return {
        alignItems: "flex-start",
        cursor: "pointer",
        position: "relative",
        backgroundColor: "transparent",
        borderRadius: 1,
        transition: "background-color 0.2s ease",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: 5,
            backgroundColor: isSelected ? "primary.main" : "transparent",
            pointerEvents: "none",
        },
        "&:focus, &:focus-visible, &:active": {
            backgroundColor: "transparent",
        },
        "&:hover": {
            backgroundColor: isSelected ? "transparent" : "action.hover",
        },
    }
}

async function resolveRecommendationContent({ existing, recommendation, projectId, scheduleId, amendsKey }) {
    const existingContent =
        existing?.payload?.content || existing?.payload?.internalContent || existing?.payload?.externalContent || ""
    const nextContent = buildRecommendationNoteContent(recommendation)

    if (!projectId || !scheduleId || !amendsKey) {
        return {
            content: mergeRecommendationNoteContent(existingContent, recommendation),
            existingAmend: existing,
        }
    }

    try {
        const merged = await mergeAuthoringRecommendationNote({
            projectId,
            scheduleId,
            amendsKey,
            nextContent,
            changeLabel: recommendation?.changeLabel,
            targetLabel: recommendation?.targetLabel,
        })

        return {
            content:
                `${merged?.content || ""}`.trim() || mergeRecommendationNoteContent(existingContent, recommendation),
            existingAmend: merged?.existingAmend || existing,
        }
    } catch (error) {
        console.warn("Falling back to local recommendation note merge", error)
        return {
            content: mergeRecommendationNoteContent(existingContent, recommendation),
            existingAmend: existing,
        }
    }
}

function buildStateLabel(state) {
    if (state === AUTHORING_RECOMMENDATION_STATE_APPLIED) return "Applied"
    if (state === AUTHORING_RECOMMENDATION_STATE_CONFLICTED) return "Conflict"
    return "Ready"
}

function buildStateColor(state) {
    if (state === AUTHORING_RECOMMENDATION_STATE_APPLIED) return "success"
    if (state === AUTHORING_RECOMMENDATION_STATE_CONFLICTED) return "warning"
    return "secondary"
}

function buildChipVariant(color) {
    if (color === "success") return "outlined"
    return "filled"
}
